import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
} from "react";
import {
  useTransition,
  animated,
  useSpring,
  AnimationResult,
  UnknownProps,
  SpringValue,
} from "@react-spring/web";
import ArtworkImageLoader from "../ArtworkImageLoader";
import { ArtworkWithTexture } from "../../types";

export interface MakeConnectionModalProps {
  onClose: () => void;
  removeArtworkId: (uuid: string) => void;
}

export interface MakeConnectionModalHandle {
  selectArtwork: (artwork: ArtworkWithTexture) => void;
  clearArtworkById: (uuid: string) => void;
}

const MakeConnectionModal = forwardRef<
  MakeConnectionModalHandle,
  MakeConnectionModalProps
>(({ onClose, removeArtworkId }, ref) => {
  const [selectedArtworks, setSelectedArtworks] = useState<
    (ArtworkWithTexture | null)[]
  >([null, null]);
  const [showConnectionAnimation, setShowConnectionAnimation] = useState(false);
  const [connectionAnimationFinished, setConnectionAnimationFinished] =
    useState(false);
  const [connectionMade, setConnectionMade] = useState(false);
  const [currentStep, setCurrentStep] = useState<
    "infoPopup" | "selectingArtwork" | null
  >("infoPopup");

  useImperativeHandle(ref, () => ({
    selectArtwork: (artwork: ArtworkWithTexture) => {
      if (
        selectedArtworks.filter((a) => a !== null).length >= 2 ||
        selectedArtworks.some((a) => a?.uuid === artwork.uuid)
      )
        return;
      const index = selectedArtworks.findIndex((a) => a === null);
      if (index !== -1) {
        const updatedArtworks = [...selectedArtworks];
        updatedArtworks[index] = artwork;
        setSelectedArtworks(updatedArtworks);
        console.log(`Artwork ${artwork.title} selected at position ${index}`);
      }
    },
    clearArtworkById: (uuid: string) => {
      const index = selectedArtworks.findIndex((a) => a?.uuid === uuid);
      if (index !== -1) {
        const updatedArtworks = [...selectedArtworks];
        updatedArtworks[index] = null;
        setSelectedArtworks(updatedArtworks);
        console.log(`Artwork with UUID ${uuid} cleared from position ${index}`);
      }
    },
  }));

  useEffect(() => {
    if (connectionMade && connectionAnimationFinished)
      handleConnectionSuccess();
  }, [connectionMade, connectionAnimationFinished]);

  const handleConnectionSuccess = () => {
    setSelectedArtworks([null, null]);
    onClose();
  };

  const clearArtwork = (index: number) => {
    const updatedArtworks = [...selectedArtworks];
    const artwork = updatedArtworks[index];
    if (artwork) removeArtworkId(artwork.uuid);
    updatedArtworks[index] = null;
    setSelectedArtworks(updatedArtworks);
  };

  const makeConnection = async () => {
    if (!canMakeConnection) return;
    setConnectionMade(true);
    setShowConnectionAnimation(true);
    try {
      await fetchConnection(
        selectedArtworks[0]!.uuid,
        selectedArtworks[1]!.uuid,
      );
    } catch (error) {
      console.error("Failed to make connection:", error);
      setConnectionMade(false);
    }
  };

  const fetchConnection = async (from: string, to: string) => {
    await fetch(
      "https://admin.collectieconnectie.nl/api/v1/artworks/make/connection",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ from, to }),
      },
    );
  };

  const canMakeConnection =
    selectedArtworks.filter((a) => a !== null).length === 2 && !connectionMade;

  const close = () => {
    setSelectedArtworks([null, null]);
    setConnectionMade(false);
    setConnectionAnimationFinished(false);
    onClose();
  };

  const transitions = useTransition(true, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { tension: 280, friction: 60 },
  });

  return transitions(
    (styles, item) =>
      item && (
        <>
          <animated.div
            style={{
              ...styles,
              pointerEvents:
                currentStep !== "selectingArtwork" ? "auto" : "none",
            }}
            className={`fixed inset-0 z-10 ${
              currentStep !== "selectingArtwork" ? "bg-black/40 " : ""
            }`}
          ></animated.div>
          {/* Overlay is only active for non-selectingArtwork steps */}
          {currentStep === "infoPopup" && (
            <InfoPopup onProceed={() => setCurrentStep("selectingArtwork")} />
          )}
          {connectionMade && <SuccessPopup onClose={handleConnectionSuccess} />}
          {currentStep === "selectingArtwork" && (
            <>
              <DesktopModalActions
                canConfirm={canMakeConnection}
                onConfirm={makeConnection}
                onClose={close}
                selectedArtworks={selectedArtworks}
                clearArtwork={clearArtwork}
              />
              <MobileModalActions
                canConfirm={canMakeConnection}
                onConfirm={makeConnection}
                onClose={close}
              />
            </>
          )}
        </>
      ),
  );
});

const InfoPopup = ({ onProceed }: { onProceed: () => void }) => {
  const transitions = useTransition(true, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  });

  return transitions(
    (styles, item) =>
      item && (
        <animated.div
          style={styles}
          className="fixed top-0 left-0 w-full h-screen flex items-center justify-center bg-black/40 z-20"
        >
          <div className="text-center p-6 border-2 border-gray-300 rounded text-white flex flex-col max-w-lg bg-black/40 z-20 backdrop--tinted">
            <h2 className="text-xl font-semibold mb-2">
              MAAK JOUW EIGEN CONNECTIE
            </h2>
            <p className="mb-2">
              Help je mee kunstwerken met elkaar te verbinden? Laat het ons
              weten als je twee kunstwerken hebt gezien die volgens jou met
              elkaar verbonden moeten worden.
            </p>
            <span className="d-icon d-network-policy text-7xl"></span>
            <span className="underline cursor-pointer" onClick={onProceed}>
              Connectie maken
            </span>
          </div>
        </animated.div>
      ),
  );
};

const SuccessPopup = ({ onClose }: { onClose: () => void }) => {
  const transitions = useTransition(true, {
    from: { opacity: 0, transform: "translateY(-20px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(-20px)" },
    config: { duration: 300 },
  });

  return transitions(
    (styles, item) =>
      item && (
        <animated.div
          style={styles}
          className="fixed top-0 left-0 w-full h-full flex items-start justify-center bg-black/40 z-30"
        >
          <div className="text-center p-8 border border-white rounded text-white flex flex-col backdrop--tinted max-w-xl mt-28 delayed-1000">
            <h2 className="text-xl font-semibold mb-2">CONNECTIE GEMAAKT</h2>
            <p className="mb-2">
              Dank je voor jouw connectie. Deze is binnenkort zichtbaar op de
              website.
            </p>
            <span className="d-icon d-check text-7xl"></span>
            <span className="underline cursor-pointer" onClick={onClose}>
              Terug naar geschiedenis
            </span>
          </div>
        </animated.div>
      ),
  );
};

const DesktopModalActions = ({
  canConfirm,
  onConfirm,
  onClose,
  selectedArtworks,
  clearArtwork,
}: {
  canConfirm: boolean;
  onConfirm: () => void;
  onClose: () => void;
  selectedArtworks: (ArtworkWithTexture | null)[];
  clearArtwork: (index: number) => void;
}) => {
  const [showModal, setShowModal] = useState(true);
  const [startLine, setStartLine] = useState(false);
  const transitions = useTransition(showModal, {
    from: { opacity: 0, transform: "translateY(100%)" },
    enter: { opacity: 1, transform: "translateY(0%)" },
    leave: { opacity: 0, transform: "translateY(100%)" },
    config: { tension: 200, friction: 20 },
    onRest: (
      result: AnimationResult<SpringValue<UnknownProps>>,
      ctrl: any,
      item: any,
    ) => {
      if (!item && result.finished === true) {
        onClose();
      }
    },
  });

  const lineSpring = useSpring({
    from: { width: "0%" },
    to: { width: startLine ? "100%" : "0%" },
    config: { duration: 1000 },
  });

  const _onConfirm = () => {
    setStartLine(true);
    setTimeout(() => {
      onConfirm();
    }, 1000);
  };

  const _onClose = () => {
    setShowModal(false);
  };

  useEffect(() => {
    setShowModal(true);
  }, []);

  return transitions(
    (styles, item) =>
      item && (
        <animated.div
          style={styles}
          className={
            "bottom-8 left-8 absolute px-6 py-4 border border-white rounded text-white !bg-black !bg-opacity-60  backdrop--tinted pr-72 z-30 hidden md:flex transition-transform"
          }
        >
          <div className="flex items-center select-none">
            <div className="w-64 border-2 relative border-white rounded h-44 bg-black drop-area">
              {selectedArtworks[0] === null && (
                <div className="p-2 absolute top-0 left-0 flex flex-col items-center justify-center h-full w-full">
                  <span className="d-icon d-add-photo mb-4 h-10 w-14"></span>
                  <div className="text-sm px-4 text-center">
                    Selecteer een kunstwerk of sleep het hier naartoe
                  </div>
                </div>
              )}
              {selectedArtworks[0] && (
                <ArtworkContainer
                  artwork={selectedArtworks[0]}
                  onClear={() => clearArtwork(0)}
                  isClearingDisabled={false}
                />
              )}
            </div>
            <div className="relative w-12 border-t border-b border-dashed border-white linker">
              <animated.span
                style={lineSpring}
                className="absolute -top-0.5 left-0 w-0 bg-white h-1 transition-all duration-1500 delay-1200"
              ></animated.span>
            </div>
            <div className="w-64 border-2 relative border-white rounded h-44 bg-black drop-area">
              {selectedArtworks[1] === null && (
                <div className="p-2 absolute top-0 left-0 flex flex-col items-center justify-center h-full w-full">
                  <span className="d-icon d-add-photo mb-4 h-10 w-14"></span>
                  <div className="text-sm px-4 text-center">
                    Selecteer een kunstwerk of sleep het hier naartoe
                  </div>
                </div>
              )}
              {selectedArtworks[1] && (
                <ArtworkContainer
                  artwork={selectedArtworks[1]}
                  onClear={() => clearArtwork(1)}
                  isClearingDisabled={false}
                />
              )}
            </div>
          </div>

          <div className="flex flex-col p-3 ml-6 absolute right-6 w-60">
            <div className="text-sm mb-3">
              1. Kies 2 kunstwerken
              <br />
              2. Bevestig jouw keuze
            </div>

            <button
              type="button"
              className={`bg-black  flex content-center items-center p-1 mb-4 border border-white rounded-full text-white text-sm ${
                canConfirm
                  ? "hover:border-green-500"
                  : "opacity-50 pointer-events-none"
              }`}
              onClick={_onConfirm}
            >
              <span className="d-icon d-check flex items-start justify-center ml-2 mr-2"></span>
              <span className="expanded-text">Bevestig keuze</span>
            </button>

            <button
              type="button"
              className="bg-black flex content-center items-center p-1 border border-white rounded-full text-white text-sm hover:border-red-500"
              onClick={_onClose}
            >
              <span className="d-icon d-close flex items-start justify-center ml-2 mr-2"></span>
              <span className="expanded-text">Stoppen</span>
            </button>
          </div>
        </animated.div>
      ),
  );
};

const MobileModalActions = ({
  canConfirm,
  onConfirm,
  onClose,
}: {
  canConfirm: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) => {
  const transitions = useTransition(true, {
    from: { opacity: 0, transform: "translateY(100%)" },
    enter: { opacity: 1, transform: "translateY(0%)" },
    leave: { opacity: 0, transform: "translateY(100%)" },
    config: { tension: 200, friction: 20 },
  });

  return transitions(
    (styles, item) =>
      item && (
        <animated.div
          style={styles}
          className={`px-2 py-2 border border-white rounded text-white bg-opacity-60 bg-black backdrop-filter backdrop--tinted transition-transform ${
            canConfirm ? "" : "translate-y-full"
          } h-0 sticky z-30 bottom-0 md:hidden`}
        >
          <div className="flex items-center">
            <div className="w-full border-2 relative border-white rounded h-20 bg-opacity-60 bg-black drop-area">
              <div className="p-2 absolute top-0 left-0 flex flex-col items-center justify-center h-full w-full">
                <span className="d-icon d-add-photo h-10 w-14"></span>
              </div>
            </div>
            <div className="relative w-12 border-t border-b border-dashed border-white linker">
              <span className="absolute top-0 left-0 w-full bg-white h-1 transition-all duration-1500 delay-1200"></span>
            </div>
            <div className="w-full border-2 relative border-white rounded h-20 bg-opacity-60 bg-black drop-area">
              <div className="p-2 absolute top-0 left-0 flex flex-col items-center justify-center h-full w-full">
                <span className="d-icon d-add-photo h-10 w-14"></span>
              </div>
            </div>
          </div>

          <div className="flex mt-3 gap-2 justify-center">
            <button
              type="button"
              className={`btn-save-connection flex content-center items-center p-1 mb-4 border border-white rounded-full text-white text-sm ${
                canConfirm
                  ? "hover:border-green-500"
                  : "opacity-50 pointer-events-none"
              }`}
              onClick={onConfirm}
            >
              <span className="d-icon d-check"></span>
              <span className="expanded-text">Bevestig keuze</span>
            </button>

            <button
              type="button"
              className="btn-cancel-connection flex content-center items-center p-1 mb-4 border border-white rounded-full text-white text-sm hover:border-red-500"
              onClick={onClose}
            >
              <span className="d-icon d-close"></span>
              <span className="expanded-text">Afbreken</span>
            </button>
          </div>
        </animated.div>
      ),
  );
};

const ArtworkContainer = ({
  artwork,
  onClear,
  isClearingDisabled,
}: {
  artwork: ArtworkWithTexture | null;
  onClear: () => void;
  isClearingDisabled: boolean;
}) => {
  const transitions = useTransition(artwork, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  });
  useEffect(() => {
    console.log(artwork);
  }, [artwork]);
  return transitions((styles, item) => (
    <animated.div
      style={styles}
      className={`w-64 border-2 relative border-white rounded h-44 drop-area ${
        !item ? "border-dashed" : ""
      }`}
    >
      {item ? (
        <div className="w-full h-full relative p-1">
          {!isClearingDisabled && (
            <span
              className="d-icon d-close absolute rounded-full flex items-center justify-center border w-5 h-5 border-white hover:text-red-500 hover:border-red-500 z-10 cursor-pointer transition-opacity duration-300"
              onClick={onClear}
            ></span>
          )}
          <ArtworkImageLoader
            className="w-full h-full object-cover rounded"
            artwork={artwork as ArtworkWithTexture}
          />
        </div>
      ) : (
        <div className="p-2 absolute top-0 left-0 flex flex-col items-center justify-center h-full w-full">
          <span className="d-icon d-add-photo mb-4 h-10 w-14"></span>
          <div className="text-sm px-4 text-center text-white">
            Selecteer een kunstwerk of sleep het hier naartoe
          </div>
        </div>
      )}
    </animated.div>
  ));
};

export default MakeConnectionModal;
